import type { z } from 'zod';

import { ProblemDetails } from '../schemas/common.js';

export interface ApiClientOptions {
  /** Base URL with no trailing slash, e.g. `https://api.gopinhos.app`. */
  baseUrl: string;
  /** Returns the current access token (or null if anonymous). */
  getAccessToken?: () => string | null | Promise<string | null>;
  /** Called once when a 401 is returned; returns true if refresh succeeded. */
  onUnauthorized?: () => Promise<boolean>;
  /** Custom fetch (default: globalThis.fetch). Useful for tests / native clients. */
  fetch?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }
}

/**
 * Minimal typed fetch wrapper. Apps can wrap this with TanStack Query etc.
 * Cookies (refresh token) are sent automatically — credentials: 'include'.
 */
export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async request<TBody, TResp extends z.ZodTypeAny>(args: {
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
    path: string;
    body?: TBody;
    query?: Record<string, string | number | boolean | undefined>;
    response?: TResp;
    /** Marks the request as one that should NOT trigger a refresh on 401 (e.g. the refresh call itself). */
    skipRefresh?: boolean;
    /** Optional idempotency key (used for /leads/:id/move). */
    idempotencyKey?: string;
  }): Promise<TResp extends z.ZodTypeAny ? z.infer<TResp> : unknown> {
    const url = this.buildUrl(args.path, args.query);
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (args.body !== undefined) headers['Content-Type'] = 'application/json';
    if (args.idempotencyKey) headers['Idempotency-Key'] = args.idempotencyKey;

    const token = await this.opts.getAccessToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;

    const fetchImpl = this.opts.fetch ?? globalThis.fetch;
    const doFetch = () =>
      fetchImpl(url, {
        method: args.method,
        headers,
        credentials: 'include',
        body: args.body !== undefined ? JSON.stringify(args.body) : undefined,
      });

    let res = await doFetch();

    if (res.status === 401 && !args.skipRefresh && this.opts.onUnauthorized) {
      const refreshed = await this.opts.onUnauthorized();
      if (refreshed) {
        const fresh = await this.opts.getAccessToken?.();
        if (fresh) headers.Authorization = `Bearer ${fresh}`;
        res = await doFetch();
      }
    }

    if (!res.ok) {
      const problem = await this.parseProblem(res);
      throw new ApiError(res.status, problem);
    }

    if (res.status === 204) return undefined as never;

    const json = (await res.json()) as unknown;
    if (args.response) return args.response.parse(json);
    return json as never;
  }

  private buildUrl(path: string, query?: Record<string, string | number | boolean | undefined>) {
    const url = new URL(path, this.opts.baseUrl);
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined) url.searchParams.set(key, String(value));
      }
    }
    return url.toString();
  }

  private async parseProblem(res: Response): Promise<ProblemDetails> {
    try {
      const data = (await res.json()) as unknown;
      return ProblemDetails.parse(data);
    } catch {
      return {
        type: 'about:blank',
        title: res.statusText || 'Request failed',
        status: res.status,
      };
    }
  }
}
